import { Check, Circle, Sparkles, X } from "lucide-react";
import { Link } from "react-router-dom";
import { useEffect, useState } from "react";

import { Button } from "components/common/buttons/Button";
import { useProjectIdentifiers } from "store/hooks/useProjectIdentifiers";
import { cn } from "utils/cn";
import { useOptionalUserPreferences } from "providers/user-preferences";

const readFirstValue = (projectId: string): boolean => {
  try {
    return Boolean(window.localStorage.getItem(`rivetai:first-value:${projectId}`));
  } catch {
    return false;
  }
};

export const ActivationChecklist = () => {
  const { project, projectId, documentId, hasValidIds } = useProjectIdentifiers(false);
  const preferencesContext = useOptionalUserPreferences();
  const persistedOnboarding = preferencesContext?.preferences.onboarding;
  const [firstValueComplete, setFirstValueComplete] = useState(() =>
    projectId
      ? readFirstValue(String(projectId)) ||
        (persistedOnboarding?.projectId === String(projectId) &&
          persistedOnboarding.projectSetupStatus === "first_value_completed")
      : false
  );
  const [dismissedLocally, setDismissedLocally] = useState(false);

  useEffect(() => {
    setDismissedLocally(false);
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    setFirstValueComplete(
      readFirstValue(String(projectId)) ||
        (persistedOnboarding?.projectId === String(projectId) &&
          persistedOnboarding.projectSetupStatus === "first_value_completed")
    );
    const handleFirstValue = (event: Event) => {
      const detail = (event as CustomEvent<{ projectId?: string }>).detail;
      if (detail?.projectId === String(projectId)) setFirstValueComplete(true);
    };
    window.addEventListener("rivetai:first-value-completed", handleFirstValue);
    return () => window.removeEventListener("rivetai:first-value-completed", handleFirstValue);
  }, [persistedOnboarding?.projectId, persistedOnboarding?.projectSetupStatus, projectId]);

  const isTrackedOnboardingProject =
    Boolean(projectId) && persistedOnboarding?.projectId === String(projectId);

  if (
    !hasValidIds ||
    !projectId ||
    project?.isDemo ||
    !isTrackedOnboardingProject ||
    persistedOnboarding?.dismissed ||
    dismissedLocally ||
    firstValueComplete
  ) {
    return null;
  }

  const screenplayReady =
    Boolean(documentId) ||
    (persistedOnboarding?.projectId === String(projectId) &&
      ["screenplay_ready", "first_value_completed"].includes(
        persistedOnboarding.projectSetupStatus ?? ""
      ));
  const steps = [
    { label: "Production created", complete: true },
    { label: "Screenplay ready", complete: screenplayReady },
    { label: "Review your first production output", complete: false },
  ];
  const breakdownPath = `/workspaces/${project?.workspaceId}/projects/${projectId}/script/breakdown`;
  const dismiss = () => {
    setDismissedLocally(true);
    preferencesContext?.patchPreferences({ onboarding: { dismissed: true } });
  };

  return (
    <aside className="relative mx-4 mt-4 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-primary/20 bg-primary/[0.04] px-4 py-3 pr-12 lg:mx-6">
      <div className="flex min-w-0 items-start gap-3">
        <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <Sparkles className="h-4 w-4" aria-hidden />
        </span>
        <div className="min-w-0">
          <p className="text-sm font-semibold text-foreground">Finish setting up this production</p>
          <ul className="mt-1.5 flex flex-wrap gap-x-4 gap-y-1">
            {steps.map((step) => {
              const Icon = step.complete ? Check : Circle;
              return (
                <li
                  key={step.label}
                  className={cn(
                    "flex items-center gap-1.5 text-xs",
                    step.complete ? "text-foreground" : "text-muted-foreground"
                  )}
                >
                  <Icon className="h-3.5 w-3.5" aria-hidden />
                  {step.label}
                </li>
              );
            })}
          </ul>
        </div>
      </div>
      <Button asChild color="brand" size="sm" className="shrink-0">
        <Link to={breakdownPath}>Review script breakdown</Link>
      </Button>
      <Button
        type="button"
        color="gray"
        size="icon"
        onClick={dismiss}
        className="absolute right-3 top-3 h-7 w-7"
        aria-label="Dismiss production setup checklist"
      >
        <X className="h-4 w-4" aria-hidden />
      </Button>
    </aside>
  );
};
